import {
  Component,
  inject,
  OnInit,
  viewChild,
  AfterViewInit,
  computed,
  OnDestroy,
} from '@angular/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { FormsModule, NgForm } from '@angular/forms';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { NgClass } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PrimeInvoiceStore } from '@app/store/prime-invoice/primeInvoice.store';
import { FloatingInputComponent } from '@app/framework/inputs/floating-input/floating-input.component';
import { InputCalendarComponent } from '@app/framework/inputs/input-calendar/input-calendar.component';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import {
  PrimeInvoiceStatus,
  PrimeInvoiceStatusOptions,
} from '@app/framework/constants/prime-invoice.constants';
import { isPersistedFile, ManagedFile } from '@app/framework/interfaces/file.type';
import { InputUploadComponent } from '@app/framework/file-handling/input-upload/input-upload.component';

@Component({
  selector: 'app-prime-invoice-sidebar-general',
  imports: [
    NgScrollbar,
    FormsModule,
    FloatingInputComponent,
    InputCalendarComponent,
    DropdownComponent,
    CdkTextareaAutosize,
    NgClass,
    InputUploadComponent,
  ],
  templateUrl: './prime-invoice-sidebar-general.component.html',
  styleUrl: './prime-invoice-sidebar-general.component.scss',
})
export class PrimeInvoiceSidebarGeneralComponent implements OnInit, AfterViewInit, OnDestroy {
  generalForm = viewChild<NgForm>('generalForm');

  invoiceStore = inject(PrimeInvoiceStore);
  model = this.invoiceStore.model;
  canBeEdited = this.invoiceStore.canBeEdited;
  statusOptions = computed(() => {
    if (this.canBeEdited()) {
      return PrimeInvoiceStatusOptions;
    }
    return PrimeInvoiceStatusOptions.filter((option) => option.value !== 'review');
  });
  customIdDisabled = true;

  invoiceNumberChanged$ = new Subject<string>();
  isDestroyed$ = new Subject<boolean>();

  ngOnInit() {
    if (!this.invoiceStore.isEdit()) {
      this.invoiceStore.getNextInvoiceNumber();
    }

    this.invoiceNumberChanged$
      .pipe(takeUntil(this.isDestroyed$), debounceTime(500))
      .subscribe((newNumber: string) => {
        this.invoiceStore.isInvoiceNumberValid(newNumber).subscribe({
          next: (isValid: boolean) => {
            if (isValid) {
              this.generalForm().form.get('invoice_number').setErrors(null);
            } else {
              this.generalForm().form.get('invoice_number').setErrors({ pattern: true });
            }
          },
        });
      });
  }

  ngAfterViewInit() {
    this.invoiceStore.setFirstPageForm(this.generalForm());
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  onStatusChange(status: PrimeInvoiceStatus) {
    if (status !== 'paid') {
      this.model().paid_date = null;
    }
  }

  invoiceNumberChanged(newNumber: string) {
    this.invoiceNumberChanged$.next(newNumber);
  }

  onCustomIdIconClick() {
    if (!this.customIdDisabled) {
      this.invoiceStore.revertToOriginalInvoiceNumber();
    }

    this.customIdDisabled = !this.customIdDisabled;
  }

  protected filesChanged(files: ManagedFile[]) {
    this.invoiceStore.updateInvoiceModel({ files });
  }

  protected removeFile({ file, index }: { file: ManagedFile; index: number }) {
    let updatedFiles: ManagedFile[] = [];
    if (isPersistedFile(file)) {
      updatedFiles = this.model().files.map((modelFile) => {
        if (isPersistedFile(modelFile) && modelFile.id === file.id) {
          return {
            ...modelFile,
            toBeDeleted: true,
          };
        }
        return modelFile;
      });
    } else {
      updatedFiles = this.model().files.filter((_, modelIndex) => modelIndex !== index);
    }

    this.invoiceStore.updateInvoiceModel({ files: updatedFiles });
  }
}
